import { registerBrick, BrickContext } from 'olympe';
import { ReactBrick, useProperty } from '@olympeio/core';
import { Box, Typography, ThemeProvider, Button } from '@mui/material';
import React, { useState, useRef, useEffect } from 'react';
import Chip from '@mui/material/Chip';
import { buttonsTheme } from './_theme.jsx';

class DisplayFilters extends ReactBrick {
    /**
     * @override
     * @protected
     * @param {!BrickContext} $
     * @returns {function(object): React.ReactElement}
     */
    static getReactComponent($) {
        return () => {
            // Constant
            const SINGLE_FILTER_LINE_HEIGHT = 77;
            const filterEnum = {
                companyCodes: { label: 'Company Code', propToDisplay: ['CompCode', 'CompCodeDesc'] },
                role: { label: 'Role', propToDisplay: ['role'] },
                agents: { label: 'Agent', propToDisplay: ['ApproverName'] },
                positions: { label: 'Position', propToDisplay: ['PosId', 'PosIdName'] },
                responsibilities: { label: 'Responsibilities', propToDisplay: ['RespNo', 'RespShort'] },
                blockingReasons: { label: 'Blocking Reason', propToDisplay: ['BlockReason', 'Description'] },
                plants: { label: 'Plant', propToDisplay: ['PlantCode', 'PlantName'] },
                materialGroups: { label: 'Material Group', propToDisplay: ['MatGroup', 'MatGroupDesc'] },
                purchasingGroups: {
                    label: 'Purchasing Group',
                    propToDisplay: ['PurchGroup', 'PurchGroupName'],
                },
                storageLocations: { label: 'Storage Location', propToDisplay: ['Lgort', 'Lgobe'] },
                materialTypes: { label: 'Material Type', propToDisplay: ['Mtart', 'Mtbez'] },
            };

            // listen to properties
            const filters = useProperty($, 'filters');
            const containerRef = useRef();
            const [isOpen, setIsOpen] = useState(false);
            const [openable, setOpenable] = useState(false);

            useEffect(() => {
                // Skip a frame to let the browser render that new text, so we can get the actual height computed by the browser
                requestAnimationFrame(() => {
                    $.set('Height', Math.min(containerRef.current.offsetHeight, SINGLE_FILTER_LINE_HEIGHT));
                    setOpenable(containerRef.current.offsetHeight > SINGLE_FILTER_LINE_HEIGHT);
                    setIsOpen(false);
                });
            }, [containerRef.current?.offsetHeight, filters]);

            const openOrClose = () => {
                if (isOpen) {
                    $.set('Height', SINGLE_FILTER_LINE_HEIGHT); // Height of a single line
                    setIsOpen(false);
                } else {
                    $.set('Height', containerRef.current.offsetHeight);
                    setIsOpen(true);
                }
            };

            const handleChipDeletion = (filter, data) => {
                const newFilters = JSON.parse(JSON.stringify(filters));
                newFilters[filter].splice(filters[filter].indexOf(data), 1);
                $.set('filters', newFilters);
                $.trigger('On Filter Deleted');
            };

            const filterItems = [];
            if (filters) {
                Object.keys(filters)
                    .filter((v) => filters[v].length > 0)
                    .forEach((filter, index) => {
                        const pills = [];
                        filters[filter].forEach((value, index) => {
                            const valueToDisplay = filterEnum[filter].propToDisplay
                                .map((prop) => value[prop])
                                .join(' - ');

                            pills.push(
                                <Chip
                                    key={`chip-${filter}-${index}`}
                                    label={valueToDisplay}
                                    color="primary"
                                    variant="outlined"
                                    style={{ marginRight: '5px', marginBottom: '5px', backgroundColor: 'white' }}
                                    onDelete={() => handleChipDeletion(filter, value)}
                                />
                            );
                        });

                        filterItems.push(
                            <Box style={{ display: 'inline-block', padding: '10px', paddingLeft: '0' }}>
                                <Typography variant="body2">
                                    {filterEnum[filter].label + (pills.length > 1 ? 's:' : ':')}
                                </Typography>
                                {pills}
                            </Box>
                        );
                    });
            }

            return (
                <Box
                    ref={containerRef}
                    sx={{
                        width: 1,
                        overflow: 'visible',
                        boxSizing: 'border-box',
                        background: '#f1910121',
                        paddingLeft: '32px',
                        paddingRight: '32px',
                    }}
                >
                    <React.Fragment>
                        <ThemeProvider theme={buttonsTheme}>
                            {openable && (
                                <Box style={{ display: 'inline-block', float: 'right', marginTop: '18px' }}>
                                    <Button variant="contained" color="primary" onClick={openOrClose}>
                                        {isOpen ? 'Close' : 'See More...'}
                                    </Button>
                                </Box>
                            )}
                            {filterItems}
                        </ThemeProvider>
                    </React.Fragment>
                </Box>
            );
        };
    }
}

registerBrick('018afb3ca4ac90c706ef', DisplayFilters);
